import type { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import cx from 'helpers/cx'
import { isSameTime } from 'helpers/date/showDate'
import { TabletUp } from 'helpers/MediaQuery'

import { DisplayDate } from 'js/components/DisplayDate'
import Flexer from 'js/components/Flexer/Flexer'
import Tag from 'js/components/Tag'
import Text from 'js/components/Text/Text'

import type { EventChildFragmentFragment } from '../EventActivities/EventChildFragment.fragment.generated'

import styles from './Activity.module.css'

/**
 * This type supports two type of queries for an event (simple and all children)
 * slots, maxAttendees and attendees only exist on the detailed child fragment
 */
type ActivityEntity = Pick<
    EventChildFragmentFragment,
    'title' | 'url' | 'startDate' | 'endDate'
> &
    Partial<
        Pick<EventChildFragmentFragment, 'slots' | 'maxAttendees' | 'attendees'>
    >

export type ActivityProps = PropsWithChildren<{
    entity: ActivityEntity
    showSlots?: boolean
    showSpots?: boolean
    isFirst?: boolean
    isLast?: boolean
}>

export const Activity = ({
    entity,
    showSlots,
    showSpots,
    isFirst,
    isLast,
    children,
}: ActivityProps) => {
    const { t } = useTranslation()

    const {
        title,
        url,
        startDate,
        endDate,
        slots = [],
        maxAttendees,
        attendees: { totalAccept } = {},
    } = entity

    const sameTime = isSameTime(startDate, endDate)

    const hasSlots = showSlots && slots.length > 0
    const hasSpots = showSpots && maxAttendees

    const spotsLeft = Number(maxAttendees) - totalAccept

    return (
        <div
            className={cx(
                styles.wrapper,
                isFirst && styles.isFirst,
                isLast && styles.isLast,
            )}
        >
            <div className={styles.activityTime} aria-hidden>
                <DisplayDate date={startDate} type="time" />
                {!!endDate && !sameTime ? (
                    <>
                        <TabletUp>&nbsp;–&nbsp;</TabletUp>
                        <DisplayDate date={endDate} type="time" side="right" />
                    </>
                ) : null}
            </div>
            <Link
                to={url}
                state={{ prevPathname: location.pathname }}
                className={styles.activityLink}
            >
                <div className={styles.activityTitle}>{title}</div>

                {(hasSlots || hasSpots) && (
                    <Flexer
                        justifyContent="flex-start"
                        gutterSize="sm"
                        wrap
                        style={hasSlots ? { marginTop: '2px' } : {}}
                    >
                        {hasSlots && (
                            <Flexer
                                justifyContent="flex-start"
                                gutterSize="sm"
                                wrap
                            >
                                {slots.map((slot, i) => (
                                    <Tag
                                        key={`${slot}-${i}`}
                                        style={{
                                            marginTop: '2px',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        {slot}
                                    </Tag>
                                ))}
                            </Flexer>
                        )}

                        {hasSpots && (
                            <Text size="small" variant="grey">
                                {spotsLeft > 0
                                    ? t('entity-event.spots-available', {
                                          count: spotsLeft,
                                      })
                                    : t('entity-event.guest-list-full')}
                            </Text>
                        )}
                    </Flexer>
                )}
            </Link>
            {children}
        </div>
    )
}
